import React from 'react';
import getInjuryStatusColor from '../../utils/getInjuryStatusColor';
import { sortStatusCountEntries } from '../../utils/injuryStatus';
import InjuryReportScopeTabs from './InjuryReportScopeTabs';
import { injuryReportPanelClassName } from './injuryReportStyles';

function StatBlock({ label, value, loading }) {
	return (
		<div>
			<p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
				{label}
			</p>
			<p className="mt-1 text-3xl font-bold tracking-tight text-gray-900 dark:text-dark-text tabular-nums">
				{loading ? '—' : value}
			</p>
		</div>
	);
}

export default function InjuryReportSummaryBar({
	meta,
	loading,
	scope,
	onScopeChange
}) {
	const statusEntries = sortStatusCountEntries(
		Object.entries(meta?.status_counts || {}).filter(([, count]) => count > 0)
	);

	if (!meta && loading) {
		return (
			<div className={`${injuryReportPanelClassName} px-6 py-5 animate-pulse`}>
				<div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
					<div className="grid grid-cols-2 gap-6 sm:grid-cols-3 flex-1">
						{[1, 2, 3].map((i) => (
							<div key={i} className="h-14 rounded-lg bg-gray-100 dark:bg-dark-border" />
						))}
					</div>
					<div className="h-10 w-64 max-w-full rounded-xl bg-gray-100 dark:bg-dark-border" />
				</div>
			</div>
		);
	}

	if (!meta) return null;

	return (
		<div className={`${injuryReportPanelClassName} px-6 py-5`}>
			<div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
				<div className="grid grid-cols-2 gap-6 sm:grid-cols-3 flex-1">
					<StatBlock
						label="Injured players"
						value={meta.total_injured ?? 0}
						loading={loading}
					/>
					<StatBlock
						label="On your rosters"
						value={meta.on_roster_count ?? 0}
						loading={loading}
					/>
					<StatBlock
						label="High impact"
						value={meta.high_impact_count ?? 0}
						loading={loading}
					/>
				</div>

				<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 lg:flex-shrink-0">
					<p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
						View
					</p>
					<InjuryReportScopeTabs
						scope={scope}
						onChange={onScopeChange}
						meta={meta}
					/>
				</div>
			</div>

			{statusEntries.length > 0 && (
				<div className="mt-5 pt-5 border-t border-gray-200 dark:border-dark-border flex flex-wrap gap-x-6 gap-y-3">
					{statusEntries.map(([label, count]) => (
						<div
							key={label}
							className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
						>
							<span
								className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
								style={{ backgroundColor: getInjuryStatusColor(label) }}
								aria-hidden
							/>
							<span>{label}</span>
							<span className="font-semibold text-gray-900 dark:text-dark-text tabular-nums">
								{count}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
